import type { components } from "../generated/schema";
import { EventSource } from "eventsource";
import { SSE_CONNECTION_ID_HEADER, getSseConnectionId } from "./sse-connection";

type ActivityDto = components["schemas"]["ReturnRequestLogDto"];

type ActivityStreamPayload = {
  activity: ActivityDto;
  sseConnectionId?: string | null;
};

export interface ActivityStreamConfig {
  shopId: string;
  returnRequestId: string;
  getAccessToken: () => string | null;
  onActivity: (activity: ActivityDto, sseConnectionId?: string) => void;
  onOpen?: () => void;
  onError?: (error: Event) => void;
  onClose?: () => void;
  maxReconnectAttempts?: number;
  sseConnectionId?: string | null;
}

export class ActivityStreamClient {
  private _eventSource: EventSource | null = null;
  private _reconnectAttempts = 0;
  private _reconnectTimeout: NodeJS.Timeout | null = null;
  private readonly _config: Required<ActivityStreamConfig>;

  constructor(config: ActivityStreamConfig) {
    this._config = {
      maxReconnectAttempts: 5,
      onOpen: () => {},
      onError: () => {},
      onClose: () => {},
      sseConnectionId: null,
      ...config,
    };
  }

  connect(): void {
    if (this._eventSource) {
      console.warn("Activity stream already connected");
      return;
    }

    const token = this._config.getAccessToken();
    if (!token) {
      console.warn("No access token available for activity stream");
      return;
    }

    const { shopId, returnRequestId } = this._config;
    // `OpenAPI.BASE` is never set since the openapi-fetch migration and stays
    // on its generated default, http://localhost:3040.
    const baseUrl = (import.meta.env.VITE_API_URL ?? "").replace(/\/$/, "");
    const url = `${baseUrl}/shops/${shopId}/return-requests/${returnRequestId}/activities/stream`;

    const sseConnectionId =
      this._config.sseConnectionId ?? getSseConnectionId();

    this._eventSource = new EventSource(url, {
      fetch: (input, init) => {
        const headers = new Headers(init?.headers ?? {});
        headers.set("Authorization", `Bearer ${token}`);
        if (sseConnectionId) {
          // headers.set(SSE_CONNECTION_ID_HEADER, sseConnectionId);
        }

        return fetch(input, {
          ...init,
          headers,
        });
      },
    });

    this._eventSource.addEventListener("activity-created", (event) => {
      try {
        const payload = JSON.parse(
          event.data as string,
        ) as ActivityStreamPayload;
        this._config.onActivity(
          payload.activity,
          payload.sseConnectionId ?? undefined,
        );
        this._reconnectAttempts = 0;
      } catch (error) {
        console.error("Failed to parse activity event:", error);
      }
    });

    this._eventSource.addEventListener("open", () => {
      console.log(
        `Activity stream connected for return request ${returnRequestId}`,
      );
      this._reconnectAttempts = 0;
      this._config.onOpen();
    });

    this._eventSource.addEventListener("error", (error) => {
      console.warn("Activity stream error:", error);
      this._config.onError(error);

      if (this._eventSource?.readyState === EventSource.CLOSED) {
        this._reconnectAttempts++;

        if (this._reconnectAttempts >= this._config.maxReconnectAttempts) {
          console.error(
            `Failed to reconnect after ${this._config.maxReconnectAttempts} attempts`,
          );
          this.disconnect();
        } else {
          const delay = Math.min(
            1000 * Math.pow(4, this._reconnectAttempts - 1),
            30000,
          );
          console.log(
            `Reconnecting in ${delay}ms (attempt ${this._reconnectAttempts}/${this._config.maxReconnectAttempts})`,
          );

          this._reconnectTimeout = setTimeout(() => {
            this.disconnect();
            this.connect();
          }, delay);
        }
      }
    });
  }

  disconnect(): void {
    if (this._reconnectTimeout) {
      clearTimeout(this._reconnectTimeout);
      this._reconnectTimeout = null;
    }

    if (this._eventSource) {
      this._eventSource.close();
      this._eventSource = null;
      console.log(
        `Activity stream disconnected for return request ${this._config.returnRequestId}`,
      );
      this._config.onClose();
    }
  }

  isConnected(): boolean {
    return (
      this._eventSource !== null &&
      this._eventSource.readyState !== EventSource.CLOSED
    );
  }
}
